import { CobaltPagination } from '@cae/cobalt-react/components/pagination';
import { CobaltTag } from '@cae/cobalt-react/components/tag';
import { CobaltClickableTile } from '@cae/cobalt-react/components/tile';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useUsersData } from '@features/users/contexts/data/useUsersData';
import { DataValue } from '@shared/components/data-value/DataValue';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import * as React from 'react';

import { useUserNavigation } from '../../../hooks/use-user-navigation/useUserNavigation';
import { getUsersStatusTagColor } from '../table/UsersTable.utils';
import styles from './UsersTableMobile.module.css';

const UsersTableMobile = React.memo((): React.ReactElement => {
  const {
    users,
    pageSize,
    pageIndex,
    handlePageIndexChange,
    handlePageSizeChange,
  } = useUsersData();
  const { email } = useAuthenticatedUser();
  const { navigateToUser } = useUserNavigation();

  const { data } = users;
  const totalItems = data.total;

  return (
    <div className={styles.usersTableMobile} data-testid="users-table-mobile">
      <p className="body-02">
        Showing {data.elements.length} of {totalItems} users
      </p>

      <div className={styles.usersMobileList}>
        {data.elements.map(user => {
          const isCurrentUser = email === user.email;

          return (
            <CobaltClickableTile
              id={`user-mobile-card-${user.id}`}
              key={user.id}
              className={styles.userMobileTile}
              onClick={(): void => navigateToUser(user.id)}
            >
              <div className={styles.userMobileHeader}>
                <h2 className="heading-01">
                  <DataValue value={user.name} />
                  {isCurrentUser && user.name && ' (you)'}
                </h2>
                {user.status ? (
                  <CobaltTag
                    color={getUsersStatusTagColor(user.status)}
                    size="small"
                  >
                    {user.status}
                  </CobaltTag>
                ) : (
                  <DataValue value={null} />
                )}
              </div>

              <div className={`${styles.userMobileContent} body-02`}>
                <div className={styles.userMobileRow}>
                  <span className={styles.label}>Email</span>
                  <span className={styles.value}>
                    <DataValue value={user.email} />
                  </span>
                </div>

                <div className={styles.userMobileRow}>
                  <span className={styles.label}>Role</span>
                  <span className={styles.value}>
                    <DataValue
                      value={user.roles ? user.roles.join(', ') : null}
                    />
                  </span>
                </div>

                <div className={styles.userMobileRow}>
                  <span className={styles.label}>Last login</span>
                  <span className={styles.value}>
                    <DataValue
                      value={
                        user.lastLogin ? formatDateShort(user.lastLogin) : null
                      }
                    />
                  </span>
                </div>

                <div className={styles.userMobileRow}>
                  <span className={styles.label}>Last modified</span>
                  <span className={styles.value}>
                    <DataValue
                      value={
                        user.lastModified
                          ? formatDateShort(user.lastModified)
                          : null
                      }
                    />
                  </span>
                </div>
              </div>
            </CobaltClickableTile>
          );
        })}
      </div>

      <CobaltPagination
        length={totalItems}
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPageIndexChange={handlePageIndexChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[10, 25, 50, 100]}
        hasPageSelect={false}
        hasItemCount={false}
      />
    </div>
  );
});

UsersTableMobile.displayName = 'UsersTableMobile';

export { UsersTableMobile };
